import React from 'react'
import { Document, Page, View, Text, Image } from '@react-pdf/renderer'
import { Lesson, QuestionSlide } from '@all-for-one/shared-types'
import { S, PdfFooter, PdfBaseProps } from './pdf-shared'
import { SECTION_MAP, getSlidesForSection, getAnswerText, getQuestionText } from './pdf-content-selection'

/**
 * The two review documents (draft / answer key) -- identical body,
 * differing only in the header badge. See
 * docs/BLUEPRINT-2026-09-30-pdf-documents-decomposition.md.
 */

// ── Shared document header ────────────────────────────────────────────────────

const DocHeader = ({
    lesson, assignedClassName, gradeBand, subject, logoDataUrl, badgeText, badgeKey,
}: PdfBaseProps & { badgeText: string; badgeKey: 'draft' | 'answerKey' }) => (
    <View style={S.docHeader} fixed>
        <View style={S.headerLeft}>
            {logoDataUrl ? <Image src={logoDataUrl} style={S.logo} /> : null}
            <View>
                <Text style={S.lessonName}>{lesson.name}</Text>
                {assignedClassName ? <Text style={S.headerDetail}>Class: {assignedClassName}</Text> : null}
                {subject ? <Text style={S.headerDetail}>Subject: {subject}</Text> : null}
                {gradeBand ? <Text style={S.headerDetail}>Grade band: {gradeBand}</Text> : null}
            </View>
        </View>
        <Text style={badgeKey === 'draft' ? S.badgeDraft : S.badgeAnswerKey}>{badgeText}</Text>
    </View>
)

// ── Draft / Answer Key body ───────────────────────────────────────────────────

function renderDraftBody(
    lesson: Lesson,
    sectionKeys: string[],
    levels: string[],
    sortOrder: 'section-level' | 'level-section',
) {
    const renderLevelBlock = (slides: QuestionSlide[], level: string, isAssessment: boolean) => {
        const rows: { label: string; text: string; answer: string }[] = []
        for (const qs of slides) {
            for (const qbd of qs.questionsByDifficulty) {
                if (qbd.difficulty !== level) continue
                for (const q of qbd.questions) {
                    let label = (q as any).displayLabel ?? ''
                    if (isAssessment && label) {
                        const pos = label.replace('.PRE.', '.POS.')
                        if (pos !== label) label = `${label} / ${pos}`
                    }
                    rows.push({ label, text: getQuestionText(q), answer: getAnswerText(q) })
                }
            }
        }
        if (rows.length === 0) return null
        return rows.map((r, i) => (
            <View key={i} style={S.question} wrap={false}>
                <View style={S.questionRow}>
                    <Text style={S.questionLabel}>{r.label} </Text>
                    <Text style={S.questionText}>{r.text}</Text>
                </View>
                <Text style={S.answerText}>Answer: {r.answer}</Text>
            </View>
        ))
    }

    if (sortOrder === 'section-level') {
        return sectionKeys.map(key => {
            const slides = getSlidesForSection(lesson.questionSlides, key)
            if (!slides.length) return null
            return (
                <View key={key}>
                    <Text style={S.sectionHeader}>{SECTION_MAP[key].label}</Text>
                    {levels.map(level => {
                        const content = renderLevelBlock(slides, level, key === 'assessment')
                        if (!content) return null
                        return (
                            <View key={level}>
                                <Text style={S.levelHeader}>{level}</Text>
                                {content}
                            </View>
                        )
                    })}
                </View>
            )
        })
    }
    return levels.map(level => (
        <View key={level}>
            <Text style={S.sectionHeader}>{level}</Text>
            {sectionKeys.map(key => {
                const slides = getSlidesForSection(lesson.questionSlides, key)
                if (!slides.length) return null
                const content = renderLevelBlock(slides, level, key === 'assessment')
                if (!content) return null
                return (
                    <View key={key}>
                        <Text style={S.levelHeader}>{SECTION_MAP[key].label}</Text>
                        {content}
                    </View>
                )
            })}
        </View>
    ))
}

export interface DraftKeyPdfProps extends PdfBaseProps {
    selectedSections: Set<string>
    selectedLevels: Set<string>
    sortOrder: 'section-level' | 'level-section'
}

export const DraftPdfDocument = (props: DraftKeyPdfProps) => {
    const { lesson, selectedSections, selectedLevels, sortOrder, downloadDate, ...rest } = props
    const sectionKeys = ['assessment', 'cfu', 'iw'].filter(k => selectedSections.has(k))
    const levels = ['Emerging', 'Intermediate', 'Advanced'].filter(l => selectedLevels.has(l))
    return (
        <Document>
            <Page style={S.page} size="LETTER">
                <DocHeader {...rest} lesson={lesson} badgeText="DRAFT" badgeKey="draft" downloadDate={downloadDate} />
                {renderDraftBody(lesson, sectionKeys, levels, sortOrder)}
                <PdfFooter lessonName={lesson.name} downloadDate={downloadDate} />
            </Page>
        </Document>
    )
}

export const AnswerKeyPdfDocument = (props: DraftKeyPdfProps) => {
    const { lesson, selectedSections, selectedLevels, sortOrder, downloadDate, ...rest } = props
    const sectionKeys = ['assessment', 'cfu', 'iw'].filter(k => selectedSections.has(k))
    const levels = ['Emerging', 'Intermediate', 'Advanced'].filter(l => selectedLevels.has(l))
    return (
        <Document>
            <Page style={S.page} size="LETTER">
                <DocHeader {...rest} lesson={lesson} badgeText="ANSWER KEY" badgeKey="answerKey" downloadDate={downloadDate} />
                {renderDraftBody(lesson, sectionKeys, levels, sortOrder)}
                <PdfFooter lessonName={lesson.name} downloadDate={downloadDate} />
            </Page>
        </Document>
    )
}
